/**
 * TourProvider — manages onboarding tour state.
 *
 * Wraps a page section and exposes tour controls via context.
 * Completion is persisted to localStorage so the tour only shows once.
 */
import * as React from 'react';
import {
  DEMO_TOUR_RESTART_EVENT,
  DEMO_TOUR_STORAGE_KEY,
  TourStep,
  TOUR_STORAGE_KEY,
} from './tour-steps';
import { TourOverlay } from './TourOverlay';

interface TourContextState {
  /** Whether the tour is currently active */
  active: boolean;
  /** Current step index */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** The current step definition */
  step: TourStep | null;
  /** Advance to next step (or finish if last) */
  next: () => void;
  /** Go back to previous step */
  prev: () => void;
  /** Dismiss the tour entirely */
  dismiss: () => void;
  /** Restart the tour */
  restart: () => void;
  /** Transition duration for overlay motion */
  transitionMs: number;
  /** Enter animation duration */
  enterMs: number;
}

const TourContext = React.createContext<TourContextState | null>(null);

export function useTour(): TourContextState {
  const ctx = React.useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within <TourProvider>');
  return ctx;
}

interface TourProviderProps {
  steps: TourStep[];
  storageKey?: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  onComplete?: () => void;
  transitionMs?: number;
  enterMs?: number;
  openDelayMs?: number;
  persistCompletion?: boolean;
  autoStart?: boolean;
}

export function TourProvider({
  steps,
  storageKey = TOUR_STORAGE_KEY,
  children,
  onDismiss,
  onComplete,
  transitionMs = 300,
  enterMs = 250,
  openDelayMs = 800,
  persistCompletion = true,
  autoStart = true,
}: TourProviderProps) {
  const [active, setActive] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);

  // Check localStorage on mount — show tour if not completed.
  // Some flows, like the waitlist sneak peek, should replay every time.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!autoStart) {
      setActive(false);
      return;
    }
    if (!persistCompletion) {
      const timer = setTimeout(() => setActive(true), openDelayMs);
      return () => clearTimeout(timer);
    }

    const completed = localStorage.getItem(storageKey);
    if (completed) return;

    // Small delay so the page renders first
    const timer = setTimeout(() => setActive(true), openDelayMs);
    return () => clearTimeout(timer);
  }, [storageKey, openDelayMs, persistCompletion, autoStart]);

  const markComplete = React.useCallback(() => {
    if (persistCompletion && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, Date.now().toString());
    }
  }, [storageKey, persistCompletion]);

  const dismiss = React.useCallback(() => {
    setActive(false);
    setCurrentStep(0);
    markComplete();
    onDismiss?.();
  }, [markComplete, onDismiss]);

  const next = React.useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      dismiss();
      onComplete?.();
    }
  }, [currentStep, steps.length, dismiss, onComplete]);

  const prev = React.useCallback(() => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const restart = React.useCallback(() => {
    if (persistCompletion && typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
    setCurrentStep(0);
    setActive(true);
  }, [storageKey, persistCompletion]);

  // The demo tour exposes restart via a window event so the sidebar footer
  // (which lives outside this provider) can trigger a replay without
  // remounting the route.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (storageKey !== DEMO_TOUR_STORAGE_KEY) return;
    const handler = () => restart();
    window.addEventListener(DEMO_TOUR_RESTART_EVENT, handler);
    return () => window.removeEventListener(DEMO_TOUR_RESTART_EVENT, handler);
  }, [storageKey, restart]);

  const value = React.useMemo<TourContextState>(
    () => ({
      active,
      currentStep,
      totalSteps: steps.length,
      step: active ? (steps[currentStep] ?? null) : null,
      next,
      prev,
      dismiss,
      restart,
      transitionMs,
      enterMs,
    }),
    [
      active,
      currentStep,
      steps,
      next,
      prev,
      dismiss,
      restart,
      transitionMs,
      enterMs,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && <TourOverlay />}
    </TourContext.Provider>
  );
}
